import { useState, useMemo, useCallback, useEffect } from 'react'
import { AttorneyData } from '../../../dtos/applicationData'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import { useRenewal } from '../../../context/renewal'
import { useCustomer } from '../../../context/customer'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  DefaultSubtitle,
} from '../../../components/application/defaultComponents'
import NewAttorneysForm from 'src/components/application/mainPagesComponents/Attorneys/NewForm'
import { hasICOCAttorneys } from 'src/utils/renewals'
import { FirmAttorneysVerifySchema } from 'src/yup/RenewalSchema'

const Verify = () => {
  const { firmData } = useCustomer()
  const { renewalData, saveRenewalData } = useRenewal()
  const [attorneys, setAttorneys] = useState<AttorneyData[]>([])
  const [departingAttorneys, setDepartingAttorneys] = useState<AttorneyData[]>([])
  const [olderAttorneysCount, setOlderAttorneysCount] = useState(0)
  const [displayErrors, setDisplayErrors] = useState(false)
  const [ , setDisableSaveandContinue] = useState(true)
  const verifyAttorneyErrorCodes: string[] = ['EC51', 'EC52', 'EC53', 'EC54', 'EC55', 'ECNA', 'EC56', 'EC58', 'EC59',
    'EC60', 'EC61', 'EC62', 'EC63', 'EC64', 'ECNA', 'ECNA', 'ECNA', 'ECNA','EC57', 'EC510']

  useEffect(() => {
    let departing: AttorneyData[] = []
    let remainingAttorneys: AttorneyData[] = []
    const departingNames = renewalData?.renewalSection?.departingAttorneys?.map(dep => dep.attorneyName)

    if (!departingNames || departingNames.length === 0) remainingAttorneys = renewalData?.attorneysSection?.attorneys ?? []
    else {
      renewalData?.attorneysSection?.attorneys?.forEach(att => {
        if (departingNames.includes([att.firstName, att.lastName].join(' '))) {
          departing.push(att)
        } else {
          remainingAttorneys.push(att)
        }
      })
    }

    setOlderAttorneysCount(remainingAttorneys.length)
    setAttorneys([...remainingAttorneys, ...(renewalData?.renewalSection?.newAttorneys ?? [])])
    setDepartingAttorneys(departing)

  }, [renewalData?.attorneysSection?.attorneys, renewalData?.renewalSection?.departingAttorneys, renewalData?.renewalSection?.newAttorneys])

  const validateAttorney = useCallback((attorney: AttorneyData) => {
    try {
      FirmAttorneysVerifySchema.validateSync(attorney)
      return true
    } catch (e) {
      return false
    }
  }, []);

  const validationPerAttorney = useMemo(() => {
    let validatedAttorney: { [key: string]: boolean } = {}

    for (let index = 1; index <= attorneys.length; index++) {
      validatedAttorney[`att__${index}`] = validateAttorney(attorneys[index - 1])
    }
    return validatedAttorney
  }, [attorneys, validateAttorney])

  const updatedApplicationData = () => ({
    ...renewalData,
    currentPage: window.location.pathname,
    attorneysSection: {
      ...renewalData?.attorneysSection,
      attorneys: [...departingAttorneys, ...attorneys.filter((att, index) => index < olderAttorneysCount)]
    },
    renewalSection: {
      ...renewalData?.renewalSection,
      newAttorneys: [...attorneys.filter((att, index) => index >= olderAttorneysCount)],
    }
  })

  const goBackRoute = () => {
    return '/customer/renew/attorneys/joined'
  }

  const goNextRoute = () => {
    if (attorneys.length === 1) {
      return '/customer/renew/attorneys/sole-practitioner'
    }

    if (hasICOCAttorneys(updatedApplicationData())) {
      return '/customer/renew/attorneys/ic-oc'
    }

    return '/customer/renew/questions/main'
  }

  const handleScrollOnMissingData = () => {
    for (let [key, isValid] of Object.entries(validationPerAttorney)) {
      if (!isValid) {
        document.getElementById(key)?.scrollIntoView({ behavior: 'smooth' })
        setDisplayErrors(true)
        break
      }
    }
  }

  const handleSaveDetails = async () => {
    handleIsFormValid()
    await saveRenewalData(updatedApplicationData())
  }

  const pageValidation = () => {
    return Object.keys(validationPerAttorney).every(key => validationPerAttorney[key] === true)
  }

  const handleIsFormValid = () => {

    let isValid = pageValidation()
    if (isValid) {
      setDisableSaveandContinue(false)
    } else {
      setDisableSaveandContinue(true)
    }
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent>
        <DefaultTitle id="new_attorneys_main_question">
          Let’s verify all information about the existing attorneys currently in {firmData.name}
        </DefaultTitle>
        <DefaultSubtitle>
          Please confirm information for all attorneys to be insured, starting with attorneys carried over from your previous policy. Newly added attorneys will be at the bottom of the page.
        </DefaultSubtitle>
        <DefaultSubtitle>
          If you see an attorney listed here who is no longer with the firm at the time of this application, please remove them by going back two pages and indicating that they have left the firm.
        </DefaultSubtitle>
        <NewAttorneysForm
          attorneys={attorneys}
          setAttorneys={setAttorneys}
          handleSaveDetails={handleSaveDetails}
          showErrors={displayErrors}
          disableAddNewAttorneys
          allAccordionsOpen
          isRenewal
          setDisableSaveandContinue={setDisableSaveandContinue}
          isAttorneyVerification={true}
          errorCodes={verifyAttorneyErrorCodes}
        />
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute={goBackRoute()}
        isFormValid={pageValidation()}
        nextRoute={goNextRoute()}
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleScrollOnMissingData}
        //disableSaveandContinue={disableSaveandContinue}
      />
    </ApplicationBodyContainer>
  )
}

export default Verify